import React, { useState, useEffect } from "react";
import RootModal from "../../../common/RootModal";
import Button from "../../../common/Button";
import CustomInput from "../../../common/CustomInput";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  relationshipTypeThirdStepOptions,
  contactOptions,
} from "../../create/options";
import { useDispatch, useSelector } from "react-redux";
import { createCaregiverDetails } from "../../../../redux/modules/patient/actions";
import {
  isFieldError,
  isNameError,
  isValidPhoneNumber,
} from "../../../../helpers/input-validations";
import {
  extractPhoneNumber,
  capitalizeFirstLetter,
} from "../../../../helpers/functions";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../common/New-ToolTip";

const isVirtualVisitType = (value = "") => value.startsWith("Virtual");

const VIRTUAL_ONSITE_TOOLTIP = (
  <div className="bg-gray-10 text-teal-300 p-2 rounded-md max-w-[300px] text-[14px]">
    <p> Pharmacist location is on site from an accredited pharmacy providing services. </p> 
  </div>
);

const VIRTUAL_REMOTE_TOOLTIP = (
  <div className="bg-gray-10 text-teal-300 p-3 rounded-md max-w-[540px] text-[14px] space-y-2">
    <h4 className="font-bold">Virtual Remote Pharmacist (Rural Pharmacy):</h4>
    <p>
      A pharmacist located at a rural-designated pharmacy providing services remotely by phone or video.
    </p>
    <p>A pharmacy is considered <strong>rural</strong> if:</p>
    <ul className="list-disc pl-4 space-y-0 [&_ul]:mt-0.5 [&_ul]:pl-4">
      <li>
        It receives a <strong>rural ODB dispensing fee</strong> (e.g., $9.93, $12.14, or $13.25), and
      </li>
      <li>
        It is located in a postal code where:
        <ul className="list-disc pl-4">
          <li>The <strong>second character</strong> is "0", or</li>
          <li>
            The location has a <strong>Rurality Index Score &gt; 40</strong>
            <span className="block pl-4">(as defined by the Ministry of Health)</span>
          </li>
        </ul>
      </li>
    </ul>
    <p>
      This classification comes from <strong>Ontario Regulation 201/96</strong> under the <strong>Ontario Drug Benefit Act.</strong>
    </p>
  </div>
);
const StartClinicalServicePopup = ({
  title = "",
  subTitle = "",
  handleClose = (serviceName = "") => {},
  onNext = (serviceName = "") => {},
}) => {
  const { patient } = useSelector((state) => state.patients);

  useEffect(() => {
    if (addCaregiver) {
      setAddCaregiver(false);
      setAttendees(
        patient.caregiver.filter((x) => x.firstName === firstNameCaregiver)[0]
          ?.id
      );
      updateClinicalService();
      onNext(StartClinicalServicePopup.displayName);
    }
  }, [patient]);

  const dispatch = useDispatch();
  const [attendeeType, setAttendeeType] = useState("patient"); // "patient", "patient-caregiver", "caregiver-only"
  const [isCaregiverAttendeeType, setIsCaregiverAttendeeType] = useState(false); // Keep for backward compatibility
  const [contactOptionsCaregiver, setContactOptionsCaregiver] = useState("");
  const [addCaregiver, setAddCaregiver] = useState(false);
  const [appointmentType, setAppointmentType] = useState("Scheduled");
  const [visitType, setVisitType] = useState("In-Person");
  const [locationType, setLocationType] = useState("Pharmacy");
  const [virtualVisitReason, setVirtualVisitReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [virtualVisitReasonError, setVirtualVisitReasonError] = useState(false);
  const [customReasonError, setCustomReasonError] = useState(false);
  const [showVirtualPlatformModal, setShowVirtualPlatformModal] = useState(false);
  const [pendingVirtualSelection, setPendingVirtualSelection] = useState(false);
  const [previousVisitType, setPreviousVisitType] = useState("In-Person");
  const [pendingVirtualVisitType, setPendingVirtualVisitType] = useState("");
  const [attendees, setAttendees] = useState("");
  const [openPrimaryContact, setOpenPrimaryContact] = useState(false);
  const careGiverOptions =
    patient?.caregiver?.map((caregiver) => {
      return {
        label: `${caregiver.firstName} ${caregiver.lastName}`,
        value: caregiver.id,
      };
    }) || [];
  const finalCareGiverOptions = [...careGiverOptions];
  const handleAttendeeTypeChange = (event) => {
    const selectedValue = event.target.value;
    setAttendeeType(selectedValue);
    // Update isCaregiverAttendeeType for backward compatibility
    setIsCaregiverAttendeeType(selectedValue !== "patient");
    // Reset addCaregiver when switching away from patient-caregiver
    if (selectedValue !== "patient-caregiver") {
      setAddCaregiver(false);
    }
  };
  const AddCaregiver = () => {
    setAddCaregiver(true);
  };
  const updateClinicalService = () => {
    const isVirtualVisit = isVirtualVisitType(visitType);
    // Map attendeeType to the correct API format
    const attendeeTypeMap = {
      "patient": "Patient",
      "patient-caregiver": "Patient & Alternate Caregiver",
      "caregiver-only": "Caregiver Only"
    };
    
    const updatedClinicalServiceObj = {
      preAssessment: {
        appointmentType: appointmentType,
        visitType: visitType,
        locationType: locationType,
        virtualVisitReason: isVirtualVisit
          ? (virtualVisitReason === "Other" ? customReason : virtualVisitReason)
          : null,
        attendeeType: attendeeTypeMap[attendeeType] || "Patient",
        attendees: (attendeeType === "patient-caregiver" || attendeeType === "caregiver-only")
          ? attendees.trim().length > 0
            ? [attendees]
            : []
          : [],
      },
    };

    dispatch({
      type: "UPDATE_CLINICAL_SERVICE_LOCAL",
      payload: updatedClinicalServiceObj,
    });
  };

  useEffect(() => {
    if (isVirtualVisitType(visitType) && (virtualVisitReason || customReason)) {
      updateClinicalService();
    }
  }, [virtualVisitReason, customReason, visitType]);

  const appointmentTypeOptions = [
    { label: "Scheduled", value: "Scheduled" },
    { label: "Walk-In", value: "Walk-In" },
  ];

  const visitTypeOptions = [
    { label: "In-Person", value: "In-Person" },
    { label: "Virtual - On Site", value: "Virtual - On Site", tooltipId: "virtual-onsite-info" },
    { label: "Virtual - Remote (Rural)", value: "Virtual - Remote (Rural Pharmacy)", tooltipId: "virtual-remote-info" },
  ];

  const locationTypeOptions = [
    { label: "Pharmacy", value: "Pharmacy" },
    { label: "Patient's Home", value: "Patient's Home" },
  ];

  // Virtual visit reason options ordered by most selected (most common first)
  // Last option "Other" will be truncated to 60 chars in dropdown display
  const virtualVisitReasonOptions = [
    { label: "Patient preference", value: "Patient preference" },
    { label: "No transportation to pharmacy", value: "No transportation to pharmacy" },
    { label: "Recent hospital discharge / mobility issue", value: "Recent hospital discharge / mobility issue" },
    { label: "Infection control or illness precautions", value: "Infection control or illness precautions" },
    { label: "Accessibility needs", value: "Accessibility needs" },
    { label: "Weather-related concern", value: "Weather-related concern" },
    { label: "Caregiver requesting on behalf of patient", value: "Caregiver requesting on behalf of patient" },
    { label: "Other (Enter a custom reason)", value: "Other", fullLabel: "Other (Enter a custom reason)" },
  ];

  const handleAppointmentTypeChange = (event) => {
    setAppointmentType(event.target.value);
  };

  const handleVisitTypeChange = (event) => {
    const selectedValue = event.target.value;
    
    if (isVirtualVisitType(selectedValue)) {
      // Store current visitType before showing modal
      setPreviousVisitType(visitType);
      setPendingVirtualVisitType(selectedValue);
      // Show modal first, don't set visitType yet
      setPendingVirtualSelection(true);
      setShowVirtualPlatformModal(true);
    } else {
      // For non-Virtual selections, set immediately
      setVisitType(selectedValue);
      // Reset virtual visit reason when switching away from Virtual
      setVirtualVisitReason("");
      setCustomReason("");
      setPendingVirtualSelection(false);
      setPendingVirtualVisitType("");
    }
  };

  const handleLocationTypeChange = (event) => {
    setLocationType(event.target.value);
  };

  const handleVirtualPlatformConfirm = () => {
    // User clicked "Okay" - confirm Virtual selection
    setVisitType(pendingVirtualVisitType || "Virtual - On Site");
    setShowVirtualPlatformModal(false);
    setPendingVirtualSelection(false);
    setPendingVirtualVisitType("");
  };

  const handleVirtualPlatformCancel = () => {
    // User clicked "Cancel" - revert to previous selection
    setShowVirtualPlatformModal(false);
    setPendingVirtualSelection(false);
    setPendingVirtualVisitType("");
    // Restore previous visitType
    setVisitType(previousVisitType);
    // Reset virtual visit reason if we're not staying on Virtual
    if (!isVirtualVisitType(previousVisitType)) {
      setVirtualVisitReason("");
      setCustomReason("");
    }
  };

  const handleVirtualVisitReasonChange = (e) => {
    setVirtualVisitReason(e.value);
    setVirtualVisitReasonError(false);
    // Reset custom reason if not "Other"
    if (e.value !== "Other") {
      setCustomReason("");
      setCustomReasonError(false);
    }
  };
  const create = true;

  const [lastNameCaregiver, setLastNameCaregiver] = useState("");
  const [middleNameCaregiver, setMiddleNameCaregiver] = useState("");
  const [firstNameCaregiver, setFirstNameCaregiver] = useState("");
  const [relationshipTypeCaregiver, setRelationshipTypeCaregiver] =
    useState("");
  const [phoneNumberCaregiver, setPhoneNumberCaregiver] = useState("");
  const [lastNameCaregiverError, setLastNameCaregiverError] = useState(false);
  const [firstNameCaregiverError, setFirstNameCaregiverError] = useState(false);
  const [relationshipTypeCaregiverError, setRelationshipTypeCaregiverError] =
    useState(false);
  const [phoneNumberCaregiverError, setPhoneNumberCaregiverError] =
    useState(false);
  const [phoneCountryCareGiver, setPhoneCountryCaregiver] = useState("+1");

  const handleInputChange = (field, value) => {
    // Clear error message when input changes
    // Update corresponding state value
    switch (field) {
      case "lastNameCaregiver":
        setLastNameCaregiver(value);
        break;
      case "middleNameCaregiver":
        setMiddleNameCaregiver(value);
        break;
      case "firstNameCaregiver":
        setFirstNameCaregiver(value);
        break;
      case "relationshipTypeCaregiver":
        setRelationshipTypeCaregiver(value);
        break;
      case "phoneNumberCaregiver":
        setPhoneNumberCaregiver(value);
        break;
      // Handle other fields
      default:
        break;
    }
  };

  const fieldsToCheck = [
    {
      field: lastNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setLastNameCaregiverError,
      alter: false,
    },
    {
      field: firstNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setFirstNameCaregiverError,
      alter: false,
    },
    {
      field: relationshipTypeCaregiver,
      checkFn: isFieldError,
      setErrorFn: setRelationshipTypeCaregiverError,
      alter: false,
    },
    {
      field: phoneNumberCaregiver,
      checkFn: isValidPhoneNumber,
      setErrorFn: setPhoneNumberCaregiverError,
      alter: true,
    },
  ];

  useEffect(() => {
    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      if (field) {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
      }
    });
  }, [...fieldsToCheck.map(({ field }) => field)]);

  const manageStep = () => {
    let allFieldsValid = true;
    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = alter ? !checkFn(field) : checkFn(field);
      setErrorFn(hasError);

      if (hasError) {
        allFieldsValid = false;
      }
    });
    if (allFieldsValid) {
      let caregiverUpdateObject = {
        firstName: firstNameCaregiver,
        middleName: middleNameCaregiver,
        lastName: lastNameCaregiver,
        relationship: relationshipTypeCaregiver,
        contactInfo: {
          primaryPhoneCountryCode: phoneCountryCareGiver,
          primaryPhone: extractPhoneNumber(phoneNumberCaregiver) || null,
          primaryContact: contactOptionsCaregiver === contactOptions[0].value,
        },
      };
      dispatch(createCaregiverDetails(patient.id, caregiverUpdateObject));
    }
  };

  return (
    <div>
      <RootModal
        //   open={modalDetail[StartClinicalServicePopup.displayName] || false}
        open={true}
        handleClose={() => handleClose(StartClinicalServicePopup.displayName)}
        className={`max-w-[650px] w-full`}
        title={title}
        subTitle={subTitle}
      >
        <div className="flex flex-col overflow-auto overflow-y-auto max-h-[600px]">
          <div className="px-6 pt-0">
            <div className="pb-2 border-b border-b-gray-100 -mt-1.5">
              <h3 className="mb-2.5 text-gray-800 font-medium text-body-lg">
                Appointment Type
              </h3>
              <div className="flex items-start gap-[50px]">
                {appointmentTypeOptions.map((option) => {
                  return (
                    <div className="flex items-start gap-2">
                      <input
                        id={option.value}
                        name="appointment-type"
                        type="radio"
                        value={option.value}
                        onChange={handleAppointmentTypeChange}
                        checked={appointmentType === option.value}
                        className="w-4 h-4  cursor-pointer  text-teal-50  focus:ring-transparent mt-0.5"
                      />
                      <label className="cursor-pointer" htmlFor={option.value}>
                        {option.label}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="pb-2 border-b border-b-gray-100 mt-2">
              <h3 className="mb-2.5 text-gray-800 font-medium  text-body-lg">
                Visit Type
              </h3>

              <div className="flex items-start gap-[56px] flex-nowrap">
                {visitTypeOptions.map((option) => {
                  return (
                    <div
                      key={option.value}
                      className="flex items-start gap-2 flex-nowrap"
                    >
                      <input
                        id={option.value}
                        name="visit-type"
                        type="radio"
                        onChange={handleVisitTypeChange}
                        value={option.value}
                        checked={visitType === option.value && !pendingVirtualSelection}
                        className="w-4 h-4 cursor-pointer text-teal-50 focus:ring-transparent mt-0.5"
                      />
                      <label className="cursor-pointer whitespace-nowrap" htmlFor={option.value}>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          {option.label}
                          {option.tooltipId && (
                            <ReactSVG
                              src="/icons/information-dark.svg"
                              className="w-4 h-4 cursor-pointer"
                              id={option.tooltipId}
                            />
                          )}
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
              <NewToolTip
                id="virtual-onsite-tooltip"
                anchorSelect="#virtual-onsite-info"
                place="bottom"
                render={() => VIRTUAL_ONSITE_TOOLTIP}
              />
              <NewToolTip
                id="virtual-remote-tooltip"
                anchorSelect="#virtual-remote-info"
                place="bottom"
                render={() => VIRTUAL_REMOTE_TOOLTIP}
              />
              {isVirtualVisitType(visitType) && (
                <div className="mt-4">
                  <CustomInput
                    options={virtualVisitReasonOptions}
                    name="virtualVisitReason"
                    className="col-span-12"
                    label="Reason For Virtual Visit"
                    placeholder="Select Reason For Virtual Visit"
                    type="dropdown"
                    onChange={handleVirtualVisitReasonChange}
                    value={virtualVisitReason}
                    error={virtualVisitReasonError}
                    customMaxHeight="max-h-[200px]"
                    dropdownInFlow={true}
                    required
                  />
                  {virtualVisitReason === "Other" && (
                    <div className="mt-4">
                      <CustomInput
                        name="customReason"
                        className="col-span-12 !bg-white"
                        label="Enter Custom Reason"
                        value={customReason}
                        placeholder="Enter custom reason"
                        type="text"
                        onChange={(e) => {
                          setCustomReason(e.target.value);
                          setCustomReasonError(false);
                        }}
                        error={customReasonError}
                        required
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="pb-2 border-b border-b-gray-100 mt-2">
              <h3 className="mb-2.5 text-gray-800 font-medium  text-body-lg">
                Location Type
              </h3>
              <div className="flex items-start gap-[54px]">
                {locationTypeOptions.map((option) => {
                  return (
                    <div key={option.value} className="flex items-start gap-2">
                      <input
                        id={option.value}
                        name="location-type"
                        type="radio"
                        value={option.value}
                        onChange={handleLocationTypeChange}
                        checked={locationType === option.value}
                        className="w-4 h-4 cursor-pointer text-teal-50 focus:ring-transparent mt-0.5"
                      />
                      <label className="cursor-pointer" htmlFor={option.value}>
                        {option.label}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-6 pt-2">
            <div className="scroll-smooth">
              <h3 className="mb-2.5 text-gray-800 font-medium  text-body-lg">
                Attendee(s)
              </h3>
              <div className="flex justify-between items-start gap-[10px]">
                <div className="flex items-start gap-2">
                  <input
                    id="patient"
                    name="Attendee-type"
                    type="radio"
                    value="patient"
                    checked={attendeeType === "patient"}
                    className="w-4 h-4  cursor-pointer  text-teal-50  focus:ring-transparent mt-0.5"
                    onChange={handleAttendeeTypeChange}
                  />
                  <label className="cursor-pointer" htmlFor="patient">
                    Patient
                  </label>
                </div>
                <div className="ml-[26px]">  
                  <div className="flex items-start gap-2">
                    <input
                      id="patient-caregiver"
                      name="Attendee-type"
                      type="radio"
                      value="patient-caregiver"
                      checked={attendeeType === "patient-caregiver"}
                      className="w-4 h-4 cursor-pointer  text-teal-50  focus:ring-transparent mt-0.5"
                      onChange={handleAttendeeTypeChange}
                    />
                    <label className="cursor-pointer" htmlFor="patient-caregiver">
                      Patient & Caregiver
                    </label>
                  </div>
                </div>
                <div className="mr-[88px]">
                  <div className="flex items-start gap-2">
                    <input
                      id="caregiver-only"
                      name="Attendee-type"
                      type="radio"
                      value="caregiver-only"
                      checked={attendeeType === "caregiver-only"}
                      className="w-4 h-4 cursor-pointer  text-teal-50  focus:ring-transparent mt-0.5"
                      onChange={handleAttendeeTypeChange}
                    />
                    <label className="cursor-pointer" htmlFor="caregiver-only">
                      Caregiver Only
                    </label>
                  </div>
                </div>
              </div>
              {(attendeeType === "patient-caregiver" || attendeeType === "caregiver-only") && (
                <div className="scroll-smooth">
                  <CustomInput
                    options={finalCareGiverOptions}
                    name="RelationshipTypeCaregiver"
                    className="col-span-12 mt-2"
                    label="Alternate Caregiver"
                    type="dropdown"
                    onChange={(e) => {
                      setAttendees(e.value);
                    }}
                    value={attendees}
                    showInputCancellation={true}
                    onClear={() => {
                      setAttendees("");
                    }}
                    dropdownInFlow={true}
                    required
                  />
                  {(attendeeType === "patient-caregiver" || attendeeType === "caregiver-only") && !addCaregiver && (
                    <button
                      onClick={AddCaregiver}
                      className="flex flex-row text-teal-600 pt-3"
                    >
                      <PlusIcon className="w-5 h-5 mt-0.5" />
                      <p className="underline">Add Alternate Caregiver</p>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="px-6 pt-2">
            {(attendeeType === "patient-caregiver" || attendeeType === "caregiver-only") && (
              <div className="scroll-smooth">
                {addCaregiver && (
                  <div className="grid grid-cols-12 gap-2 pt-2">
                    <p className="col-span-12">Add Alternate Caregiver</p>
                    <CustomInput
                      name="lastNameCaregiver"
                      className="col-span-4 !bg-white"
                      label="Last Name"
                      value={lastNameCaregiver}
                      error={lastNameCaregiverError}
                      placeholder="Last name"
                      type="text"
                      onChange={(e) =>
                        handleInputChange("lastNameCaregiver", e.target.value)
                      }
                      disabled={attendees !== ""}
                      required
                    />
                    <CustomInput
                      name="middleNameCaregiver"
                      className="col-span-4 !bg-white"
                      label="Middle Name"
                      value={middleNameCaregiver}
                      placeholder="Middle name"
                      type="text"
                      onChange={(e) =>
                        handleInputChange("middleNameCaregiver", e.target.value)
                      }
                      disabled={attendees !== ""}
                      showOptionalLabel={false}
                    />
                    <CustomInput
                      name="firstNameCaregiver"
                      className="col-span-4 !bg-white"
                      label="First Name"
                      value={firstNameCaregiver}
                      error={firstNameCaregiverError}
                      placeholder="First name"
                      type="text"
                      onChange={(e) =>
                        handleInputChange("firstNameCaregiver", e.target.value)
                      }
                      disabled={attendees !== ""}
                      required
                    />
                    <CustomInput
                      options={relationshipTypeThirdStepOptions}
                      name="RelationshipTypeCaregiver"
                      className="col-span-12 !bg-white"
                      label="Relationship"
                      type="dropdown"
                      onChange={(e) =>
                        handleInputChange("relationshipTypeCaregiver", e.value)
                      }
                      value={relationshipTypeCaregiver}
                      error={relationshipTypeCaregiverError}
                      disabled={attendees !== ""}
                      required
                    />
                    <CustomInput
                      name="phoneNumberCaregiver"
                      className="col-span-12 !bg-white"
                      label="Primary Phone Number"
                      value={phoneNumberCaregiver}
                      selectValue={phoneCountryCareGiver}
                      handleSelect={(e) =>
                        setPhoneCountryCaregiver(e.target.value)
                      }
                      error={phoneNumberCaregiverError}
                      onChange={(e) =>
                        handleInputChange(
                          "phoneNumberCaregiver",
                          e.target.value
                        )
                      }
                      placeholder="(555) 000-0000"
                      disabled={attendees !== ""}
                      type="phone"
                      required
                    />
                    <CustomInput
                      options={contactOptions}
                      type="radio"
                      name="contactOptionsCaregiver"
                      className="col-span-12"
                      onChange={(e) => {
                        setContactOptionsCaregiver(e.target.value);
                        if (
                          e.target.value === contactOptions[0].value &&
                          firstNameCaregiver?.length > 0 &&
                          lastNameCaregiver?.length > 0
                        )
                          setOpenPrimaryContact(true);
                      }}
                      value={contactOptionsCaregiver}
                      extraRadioSpace={true}
                      disabled={attendees !== ""}
                    />
                    <ConfirmationModalNew
                      open={openPrimaryContact}
                      className="w-[80%]"
                      title={"Primary Contact"}
                      type={"primary"}
                      confirmText={"Yes, Continue"}
                      content={() => (
                        <>
                          Are you sure you want to save{" "}
                          <b>
                            {capitalizeFirstLetter(lastNameCaregiver)},{" "}
                            {capitalizeFirstLetter(firstNameCaregiver)}
                          </b>{" "}
                          as your primary contact? This will mean that you will
                          no longer be the primary contact.
                        </>
                      )}
                      handleClose={() => setOpenPrimaryContact(false)}
                      confirmAction={() => setOpenPrimaryContact(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={`grid grid-cols-2 gap-5 px-6 pt-2 pb-6`}>
          <Button
            type="primary-outline"
            text={"Cancel"}
            onClick={() => handleClose(StartClinicalServicePopup.displayName)}
          />
          <Button
            type={"primary"}
            text={"Next"}
            onClick={() => {
              if (addCaregiver) manageStep();
              else {
                // Validate virtual visit reason if Virtual is selected
                if (isVirtualVisitType(visitType)) {
                  if (!virtualVisitReason) {
                    // Reason is required but not selected
                    setVirtualVisitReasonError(true);
                    return;
                  }
                  if (virtualVisitReason === "Other" && !customReason.trim()) {
                    // Custom reason is required but not entered
                    setCustomReasonError(true);
                    return;
                  }
                }
                updateClinicalService();
                onNext(StartClinicalServicePopup.displayName);
              }
            }}
          />
        </div>
        <ConfirmationModalNew
          open={showVirtualPlatformModal}
          title="Virtual Platform"
          titleClassName="font-bold text-[24px]"
          type="primary"
          confirmText="Okay"
          cancelText="Cancel"
          cancelBtnType="primary-outline"
          content={() => (
            <p className="text-gray-800 text-base font-normal">
              Please use your pharmacy organization's virtual platform. By selecting "<span className="font-semibold">Virtual</span>" option for the visit type, you automatically agree R.E.E.L Outline Inc. is not responsible or liable for any outcomes resulting from its use.
            </p>
          )}
          className="font-semibold text-gray-800 text-lg w-[537px]"
          onCross={handleVirtualPlatformCancel}
          handleClose={handleVirtualPlatformCancel}
          confirmAction={handleVirtualPlatformConfirm}
        />
      </RootModal>
    </div>
  );
};

StartClinicalServicePopup.displayName = "StartClinicalServicePopup";

export default StartClinicalServicePopup;
